import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { submitFinalClosure } from "../../actions/event/EventActions";

// DTO reference:
// Normal Bid: Id, SalvageStatus, VisitDone, GSTInvoiceGenerated, PIDNumber, PaymentDetails, ClosureDate, FinalQuantity, UnitOfFinalQuantity, BuyerType, InvoiceType, FinalAmount
// No Bid: Id, NoBidRemark, ClosureDate

const FinalClosureForm = ({ event }) => {
  const dispatch = useDispatch();
  
  // Get current date in IST
  const getISTDate = () => {
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
    const istDate = new Date(now.getTime() + istOffset);
    return istDate.toISOString().slice(0, 10); // YYYY-MM-DD format
  };
  
  const [form, setForm] = useState({
    SalvageStatus: "",
    VisitDone: "",
    GSTInvoiceGenerated: "",
    PIDNumber: "",
    PaymentDetails: "",
    ClosureDate: getISTDate(),
    FinalQuantity: "",
    UnitOfFinalQuantity: "",
    BuyerType: "",
    InvoiceType: "",
    NoBidRemark: "",
    FinalAmount: event.highestBidAmount || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Check if there's no bid data
  const hasNoBid = !event.highestBidder || !event.highestBidAmount;

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    if (type === "radio") {
      setForm({ ...form, [name]: value === "yes" });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (hasNoBid) {
      // Validation for no bid scenario
      if (!form.NoBidRemark || form.NoBidRemark.trim() === '') {
        errors.NoBidRemark = "This Field is Required";
      }

      if (!form.ClosureDate) {
        errors.ClosureDate = "This Field is Required";
      }
    } else {
      // Validation for normal scenario with bid data
      
      // Salvage Status validation
      if (!form.SalvageStatus || form.SalvageStatus === '') {
        errors.SalvageStatus = "This Field is Required";
      }

      // Visit Done validation
      if (form.VisitDone === '' || form.VisitDone === null || form.VisitDone === undefined) {
        errors.VisitDone = "This Field is Required";
      }

      // GST Invoice Generated validation
      if (form.GSTInvoiceGenerated === '' || form.GSTInvoiceGenerated === null || form.GSTInvoiceGenerated === undefined) {
        errors.GSTInvoiceGenerated = "This Field is Required";
      }

      // PID Number validation (removed - now optional)

      // Final Quantity validation
      if (!form.FinalQuantity || form.FinalQuantity === '') {
        errors.FinalQuantity = "This Field is Required";
      }

      // Unit of Final Quantity validation
      if (!form.UnitOfFinalQuantity || form.UnitOfFinalQuantity === '') {
        errors.UnitOfFinalQuantity = "This Field is Required";
      }

      // Buyer Type validation
      if (!form.BuyerType || form.BuyerType === '') {
        errors.BuyerType = "This Field is Required";
      }

      // Payment Details validation (removed - now optional)

      // Closure Date validation
      if (!form.ClosureDate) {
        errors.ClosureDate = "This Field is Required";
      }

      // Invoice Type validation
      if (!form.InvoiceType || form.InvoiceType === '') {
        errors.InvoiceType = "This Field is Required";
      }

      // Final Amount validation
      if (!form.FinalAmount || form.FinalAmount === '') {
        errors.FinalAmount = "This Field is Required";
      }
    }

    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateForm();
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      // Scroll to first error
      const firstErrorField = document.querySelector('.error-message');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    setSubmitting(true);
    setValidationErrors({});
    
    // Build formValues for API
    let formValues;
    
    // Convert date to IST datetime string
    const convertToISTDateTime = (dateStr) => {
      const date = new Date(dateStr);
      const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
      const istDate = new Date(date.getTime() + istOffset);
      return istDate.toISOString();
    };
    
    if (hasNoBid) {
      // For no bid scenario, send minimal data
      formValues = {
        Id: event.id,
        NoBidRemark: form.NoBidRemark.trim(),
        ClosureDate: convertToISTDateTime(form.ClosureDate),
      };
    } else {
      // For normal scenario with bid data
      const finalAmountValue = form.FinalAmount && form.FinalAmount !== '' ? Number(form.FinalAmount) : null;
      formValues = {
        Id: event.id,
        SalvageStatus: form.SalvageStatus,
        VisitDone: form.VisitDone,
        GSTInvoiceGenerated: form.GSTInvoiceGenerated,
        PIDNumber: form.PIDNumber ? form.PIDNumber.trim() : '',
        PaymentDetails: form.PaymentDetails ? form.PaymentDetails.trim() : '',
        ClosureDate: convertToISTDateTime(form.ClosureDate),
        FinalQuantity: Number(form.FinalQuantity),
        UnitOfFinalQuantity: form.UnitOfFinalQuantity,
        BuyerType: form.BuyerType,
        InvoiceType: form.InvoiceType,
        FinalAmount: finalAmountValue,
      };
    }
    
    dispatch(submitFinalClosure(formValues)).then(() => {
      // After successful submission, reload the page
      window.location.reload();
    }).catch(error => {
      setSubmitting(false);
      // Handle error - you might want to show an error message to the user
      console.error('Error submitting final closure:', error);
    });
  };

  return (
    <div className="card-footer overflow-hidden">
      <div className="container-fluid p-2 p-sm-3 p-md-4" style={{background: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)'}}>
        <h2 className="mb-3" style={{fontWeight: 700, color: '#233876', fontSize: 'clamp(1.25rem, 3vw, 1.75rem)'}}>Final Closure Queue</h2>
        
        {/* Card-based display for event info - responsive */}
        <div className="card mb-4" style={{border: '1px solid #dee2e6'}}>
          <div className="card-body p-3">
            <div className="row g-3">
              <div className="col-6 col-sm-4 col-md-3 col-lg-2">
                <div className="d-flex flex-column">
                  <small className="text-muted fw-bold mb-1">EVENT ID</small>
                  <span className="text-orange fw-semibold">{event.eventId}</span>
                </div>
              </div>
              <div className="col-6 col-sm-4 col-md-3 col-lg-2">
                <div className="d-flex flex-column">
                  <small className="text-muted fw-bold mb-1">CLAIM NO</small>
                  <span className="text-orange fw-semibold">{event.claimNo}</span>
                </div>
              </div>
              <div className="col-6 col-sm-4 col-md-3 col-lg-3">
                <div className="d-flex flex-column">
                  <small className="text-muted fw-bold mb-1">BIDDER</small>
                  <span className="fw-semibold">{event.highestBidder || 'NA'}</span>
                </div>
              </div>
              <div className="col-6 col-sm-6 col-md-3 col-lg-3">
                <div className="d-flex flex-column">
                  <small className="text-muted fw-bold mb-1">AWARDED AMOUNT</small>
                  <span className="fw-semibold">&#8377;{event.highestBidAmount ? event.highestBidAmount.toLocaleString('en-IN') : 'NA'}</span>
                </div>
              </div>
              <div className="col-6 col-sm-6 col-md-12 col-lg-2">
                <div className="d-flex flex-column">
                  <small className="text-muted fw-bold mb-1">AWARDED DATE</small>
                  <span className="fw-semibold">{event.modifiedDate ? event.modifiedDate.split("T")[0] : 'NA'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <form className="row g-3" onSubmit={handleSubmit}>
          {hasNoBid ? (
            // Show only No Bid Remark field when there's no bidder or awarded amount
            <>
              <div className="col-12">
                <div className="alert alert-warning" role="alert">
                   This event has no bidder or awarded amount.
                </div>
              </div>
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label fw-bold">Closure Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="ClosureDate"
                  value={form.ClosureDate}
                  readOnly
                />

                {validationErrors.ClosureDate && (
                  <div className="error-message text-danger mt-1" style={{fontSize: '0.875rem'}}>
                    {validationErrors.ClosureDate}
                  </div>
                )}
              </div>
              <div className="col-12">
                <label className="form-label fw-bold">No Bid Remark <span className="text-danger">*</span></label>
                <textarea 
                  className={`form-control ${validationErrors.NoBidRemark ? 'is-invalid' : ''}`}
                  name="NoBidRemark" 
                  rows="4"
                  value={form.NoBidRemark} 
                  onChange={handleChange}
                  placeholder="Enter remarks for no bid scenario..."
                />
                {validationErrors.NoBidRemark && (
                  <div className="error-message text-danger mt-1" style={{fontSize: '0.875rem'}}>
                    {validationErrors.NoBidRemark}
                  </div>
                )}
              </div>
            </>
          ) : (
            // Show full form when bid data exists
            <>
          <div className="col-12 col-lg-6">
            <label className="form-label fw-bold">Request Type <span className="text-danger">*</span></label>
            <select className={`form-select ${validationErrors.SalvageStatus ? 'is-invalid' : ''}`} name="SalvageStatus" value={form.SalvageStatus} onChange={handleChange}>
              <option value="">Select Request Type</option>
              <option>Notional Salvage</option>
              <option>Disposed to Buyer</option>
              <option>Retained by Insured</option>
              <option>Disposed to Surveyor Buyer</option>
              <option>No Scope of Salvage</option>
            </select>
            {validationErrors.SalvageStatus && (
              <div className="error-message text-danger mt-1" style={{fontSize: '0.875rem'}}>
                {validationErrors.SalvageStatus}
              </div>
            )}
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <label className="form-label fw-bold">Field Visit Done <span className="text-danger">*</span></label>
            <div>
              <div className="form-check form-check-inline">
                <input className="form-check-input" type="radio" name="VisitDone" id="fieldVisitYes" value="yes" checked={form.VisitDone === true} onChange={handleChange} />
                <label className="form-check-label" htmlFor="fieldVisitYes">Yes</label>
              </div>
              <div className="form-check form-check-inline">
                <input className="form-check-input" type="radio" name="VisitDone" id="fieldVisitNo" value="no" checked={form.VisitDone === false} onChange={handleChange} />
                <label className="form-check-label" htmlFor="fieldVisitNo">No</label>
              </div>
            </div>
            {validationErrors.VisitDone && (
              <div className="error-message text-danger mt-1" style={{fontSize: '0.875rem'}}>
                {validationErrors.VisitDone}
              </div>
            )}
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <label className="form-label fw-bold">GST Invoice Generated <span className="text-danger">*</span></label>
            <div>
              <div className="form-check form-check-inline">
                <input className="form-check-input" type="radio" name="GSTInvoiceGenerated" id="gstYes" value="yes" checked={form.GSTInvoiceGenerated === true} onChange={handleChange} />
                <label className="form-check-label" htmlFor="gstYes">Yes</label>
              </div>
              <div className="form-check form-check-inline">
                <input className="form-check-input" type="radio" name="GSTInvoiceGenerated" id="gstNo" value="no" checked={form.GSTInvoiceGenerated === false} onChange={handleChange} />
                <label className="form-check-label" htmlFor="gstNo">No</label>
              </div>
            </div>
            {validationErrors.GSTInvoiceGenerated && (
              <div className="error-message text-danger mt-1" style={{fontSize: '0.875rem'}}>
                {validationErrors.GSTInvoiceGenerated}
              </div>
            )}
          </div>
          <div className="col-12 col-lg-6">
            <label className="form-label fw-bold">PID Number</label>
            <input type="text" className={`form-control ${validationErrors.PIDNumber ? 'is-invalid' : ''}`} name="PIDNumber" value={form.PIDNumber} onChange={handleChange} placeholder="Enter PID Number" />
            {validationErrors.PIDNumber && (
              <div className="error-message text-danger mt-1" style={{fontSize: '0.875rem'}}>
                {validationErrors.PIDNumber}
              </div>
            )}
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <label className="form-label fw-bold">Final Quantity <span className="text-danger">*</span></label>
            <input type="number" className={`form-control ${validationErrors.FinalQuantity ? 'is-invalid' : ''}`} name="FinalQuantity" min="0" step="1" value={form.FinalQuantity} onChange={handleChange} placeholder="Enter whole number" />
            {validationErrors.FinalQuantity && (
              <div className="error-message text-danger mt-1" style={{fontSize: '0.875rem'}}>
                {validationErrors.FinalQuantity}
              </div>
            )}
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <label className="form-label fw-bold">Unit of Final Quantity <span className="text-danger">*</span></label>
            <select className={`form-select ${validationErrors.UnitOfFinalQuantity ? 'is-invalid' : ''}`} name="UnitOfFinalQuantity" value={form.UnitOfFinalQuantity} onChange={handleChange}>
              <option value="">Select Unit</option>
              <option>Kg</option>
              <option>Metric Ton</option>
              <option>Litre</option>
              <option>Bags</option>
              <option>Units</option>
              <option>Lots</option>
            </select>
            {validationErrors.UnitOfFinalQuantity && (
              <div className="error-message text-danger mt-1" style={{fontSize: '0.875rem'}}>
                {validationErrors.UnitOfFinalQuantity}
              </div>
            )}
          </div>
          <div className="col-12 col-lg-6">
            <label className="form-label fw-bold">Buyer Type <span className="text-danger">*</span></label>
            <select className={`form-select ${validationErrors.BuyerType ? 'is-invalid' : ''}`} name="BuyerType" value={form.BuyerType} onChange={handleChange}>
              <option value="">Select Buyer Type</option>
              <option>E Waste Buyer</option>
              <option>SLA Buyer</option>
              <option>Insured</option>
              <option>Surveyor's Buyer</option>
              <option>Other</option>
              <option>Empanelled Buyer</option>
              <option>End User</option>
              <option>Insured Participation</option>
            </select>
            {validationErrors.BuyerType && (
              <div className="error-message text-danger mt-1" style={{fontSize: '0.875rem'}}>
                {validationErrors.BuyerType}
              </div>
            )}
          </div>
          <div className="col-12 col-lg-6">
            <label className="form-label fw-bold">Payment/NEFT Details</label>
            <input type="text" className={`form-control ${validationErrors.PaymentDetails ? 'is-invalid' : ''}`} name="PaymentDetails" value={form.PaymentDetails} onChange={handleChange} placeholder="Enter Payment/NEFT Details" />
            {validationErrors.PaymentDetails && (
              <div className="error-message text-danger mt-1" style={{fontSize: '0.875rem'}}>
                {validationErrors.PaymentDetails}
              </div>
            )}
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <label className="form-label fw-bold">Closure Date</label>
            <input type="date" className={`form-control ${validationErrors.ClosureDate ? 'is-invalid' : ''}`} name="ClosureDate" value={form.ClosureDate} readOnly />
            {validationErrors.ClosureDate && (
              <div className="error-message text-danger mt-1" style={{fontSize: '0.875rem'}}>
                {validationErrors.ClosureDate}
              </div>
            )}
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <label className="form-label fw-bold">Invoices <span className="text-danger">*</span></label>
            <select className={`form-select ${validationErrors.InvoiceType ? 'is-invalid' : ''}`} name="InvoiceType" value={form.InvoiceType} onChange={handleChange}>
              <option value="">Select Invoice Type</option>
              <option>GST Invoice</option>
              <option>No Invoice</option>
            </select>
            {validationErrors.InvoiceType && (
              <div className="error-message text-danger mt-1" style={{fontSize: '0.875rem'}}>
                {validationErrors.InvoiceType}
              </div>
            )}
          </div>
          <div className="col-12 col-sm-6 col-lg-3">
            <label className="form-label fw-bold">Final Amount <span className="text-danger">*</span></label>
            <input 
              type="number" 
              className={`form-control ${validationErrors.FinalAmount ? 'is-invalid' : ''}`}
              name="FinalAmount" 
              min="0"
              step="0.01"
              value={form.FinalAmount} 
              onChange={handleChange}
              placeholder="Enter final amount"
            />
            {validationErrors.FinalAmount && (
              <div className="error-message text-danger mt-1" style={{fontSize: '0.875rem'}}>
                {validationErrors.FinalAmount}
              </div>
            )}
          </div>
          </>
          )}
          <div className="col-12 d-flex justify-content-end mt-3">
            {/* <button type="button" className="btn btn-secondary me-2" disabled={submitting}>Cancel</button> */}
            <button type="submit" className="btn btn-primary" disabled={submitting}>Submit</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FinalClosureForm;
